import { createContext, useContext, useState, useCallback, useRef, useEffect, ReactNode } from 'react';

interface ProcessingContextType {
  isProcessing: boolean;
  setIsProcessing: (state: boolean) => void;
  processingTimeout: number | null;
  setProcessingTimeout: (timeout: number | null) => void;
  clearProcessingTimeout: () => void;
  processingMessage: string;
  setProcessingMessage: (message: string) => void;
  hasError: boolean;
  setError: (error: boolean) => void;
  resetProcessingState: () => void;
}

const ProcessingContext = createContext<ProcessingContextType | undefined>(undefined);

const DEFAULT_TIMEOUT = 300000; // 5 minutes

export function ProcessingProvider({ children }: { children: ReactNode }) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [hasError, setError] = useState(false);
  const timeoutRef = useRef<number | null>(null);

  const setProcessingTimeout = useCallback((timeout: number | null) => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
    }

    if (timeout) {
      timeoutRef.current = window.setTimeout(() => {
        setError(true);
        setIsProcessing(false);
        setProcessingMessage('Operation timed out. Please try again.');
      }, timeout);
    }
  }, []);

  const clearProcessingTimeout = useCallback(() => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const resetProcessingState = useCallback(() => {
    setIsProcessing(false);
    setProcessingMessage('');
    setError(false);
    clearProcessingTimeout();
  }, [clearProcessingTimeout]);

  // Set default timeout when processing starts
  useEffect(() => {
    if (isProcessing && !timeoutRef.current) {
      setProcessingTimeout(DEFAULT_TIMEOUT);
    } else if (!isProcessing && timeoutRef.current) {
      clearProcessingTimeout();
    }
  }, [isProcessing, setProcessingTimeout, clearProcessingTimeout]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <ProcessingContext.Provider
      value={{
        isProcessing,
        setIsProcessing,
        processingTimeout: timeoutRef.current,
        setProcessingTimeout,
        clearProcessingTimeout,
        processingMessage,
        setProcessingMessage,
        hasError,
        setError,
        resetProcessingState,
      }}
    >
      {children}
    </ProcessingContext.Provider>
  );
}

export function useProcessing() {
  const context = useContext(ProcessingContext);
  if (context === undefined) {
    throw new Error('useProcessing must be used within a ProcessingProvider');
  }
  return context;
}